/**
 * googleDrive.js — "Save to my Drive" for generated invoice/contract files.
 *
 * Uses the drive.file scope (see auth/useAuth.js) — this app can only see or
 * write files it creates itself via this API, never browse the user's
 * existing Drive, EXCEPT for the folder the user explicitly picks below
 * (the Picker API grants scoped, one-time access to just that selection —
 * still not a general Drive-browsing permission).
 */

const DRIVE_FILES_URL  = 'https://www.googleapis.com/drive/v3/files';
const DRIVE_UPLOAD_URL = 'https://www.googleapis.com/upload/drive/v3/files';
const PICKER_API_KEY = import.meta.env.VITE_GOOGLE_API_KEY;

let pickerApiLoadPromise = null;

/** Loads the `apis.google.com/js/api.js` script + the picker module, once. */
function loadPickerApi() {
  if (pickerApiLoadPromise) return pickerApiLoadPromise;
  pickerApiLoadPromise = new Promise((resolve, reject) => {
    if (window.google?.picker) return resolve();
    const script = document.createElement('script');
    script.src = 'https://apis.google.com/js/api.js';
    script.onload = () => {
      window.gapi.load('picker', { callback: resolve, onerror: reject });
    };
    script.onerror = () => reject(new Error('Failed to load Google API script'));
    document.head.appendChild(script);
  });
  return pickerApiLoadPromise;
}

/**
 * Opens Google's folder picker so the user chooses exactly where a file
 * gets saved, instead of everything landing in Drive's root.
 * @param {string} accessToken  from useAuth's getToken() — must include drive.file scope
 * @returns {Promise<{id: string, name: string} | null>} null if the user cancels
 */
export async function pickDriveFolder(accessToken) {
  if (!PICKER_API_KEY) {
    throw new Error('Folder picker is not configured yet (VITE_GOOGLE_API_KEY missing) — saving to Drive root instead.');
  }
  await loadPickerApi();

  return new Promise((resolve, reject) => {
    try {
      const view = new window.google.picker.DocsView(window.google.picker.ViewId.FOLDERS)
        .setSelectFolderEnabled(true)
        .setIncludeFolders(true)
        .setMimeTypes('application/vnd.google-apps.folder');

      const picker = new window.google.picker.PickerBuilder()
        .addView(view)
        .setOAuthToken(accessToken)
        .setDeveloperKey(PICKER_API_KEY)
        .setTitle('Choose a Drive folder')
        .setCallback((data) => {
          if (data.action === window.google.picker.Action.PICKED) {
            const doc = data.docs[0];
            resolve({ id: doc.id, name: doc.name });
          } else if (data.action === window.google.picker.Action.CANCEL) {
            resolve(null);
          }
        })
        .build();
      picker.setVisible(true);
    } catch (e) {
      reject(e);
    }
  });
}

/**
 * @param {string} accessToken     from useAuth's getToken() — must include drive.file scope
 * @param {string} filename
 * @param {Blob}   blob
 * @param {string} [parentFolderId] from pickDriveFolder() — omit to save to Drive root
 * @returns {Promise<{id: string, webViewLink: string}>}
 */
export async function saveBlobToDrive(accessToken, filename, blob, parentFolderId) {
  const createRes = await fetch(DRIVE_FILES_URL, {
    method: 'POST',
    headers: { Authorization: `Bearer ${accessToken}`, 'Content-Type': 'application/json' },
    body: JSON.stringify({ name: filename, ...(parentFolderId ? { parents: [parentFolderId] } : {}) }),
  });
  if (!createRes.ok) {
    const body = await createRes.json().catch(() => ({}));
    throw new Error(body?.error?.message || `Drive create failed: HTTP ${createRes.status}`);
  }
  const { id } = await createRes.json();

  const uploadRes = await fetch(`${DRIVE_UPLOAD_URL}/${id}?uploadType=media&fields=id,webViewLink`, {
    method: 'PATCH',
    headers: { Authorization: `Bearer ${accessToken}`, 'Content-Type': blob.type || 'application/octet-stream' },
    body: blob,
  });
  if (!uploadRes.ok) {
    const body = await uploadRes.json().catch(() => ({}));
    throw new Error(body?.error?.message || `Drive upload failed: HTTP ${uploadRes.status}`);
  }
  return uploadRes.json();
}
